/**
 * @fileoverview Reusable Slider Component
 * Extracted from ProductGallery for general slider functionality
 * Supports percentage-based slide widths and various content types
 * 
 * 📡 EVENT SUMMARY:
 * 
 * EVENTS DISPATCHED:
 * - slider:slide-changed → Emitted when slide changes
 * - slider:ready         → Emitted when slider is initialized
 * - slider:drag-start    → Emitted when drag starts
 * - slider:drag-end      → Emitted when drag ends
 * 
 * EVENT DATA STRUCTURE:
 * - slider:slide-changed: { 
 *     currentIndex: number, 
 *     totalSlides: number,
 *     slideWidth: number,
 *     element: HTMLElement
 *   }
 * - slider:ready: {
 *     element: HTMLElement,
 *     config: Object,
 *     totalSlides: number
 *   }
 */

export class SliderComponent {
  constructor(element, options = {}) {
    this.element = element;
    this.options = {
      trackSelector: '[data-slider-track]',
      slideSelector: '[data-slider-slide]',
      prevButtonSelector: '[data-slider-prev]',
      nextButtonSelector: '[data-slider-next]',
      dotsContainerSelector: '[data-slider-dots]',
      enableDrag: true,
      enableKeyboard: true,
      enableDots: false,
      autoplay: false,
      autoplayDelay: 5000,
      loop: false,
      adaptiveHeight: false,
      ...options
    };

    // State
    this.currentIndex = 0;
    this.totalSlides = 0;
    this.slideWidth = 0;
    this.slidesPerView = 1;
    this.isDragging = false;
    this.dragStartX = 0;
    this.dragStartY = 0;
    this.dragStartIndex = 0;
    this.isHorizontalDrag = false;
    this.autoplayInterval = null;

    // DOM elements
    this.track = null;
    this.slides = null;
    this.prevButton = null;
    this.nextButton = null;
    this.dotsContainer = null;
    this.dots = [];

    // Bind methods
    this.handleResize = this.handleResize.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.startDrag = this.startDrag.bind(this);
    this.handleDrag = this.handleDrag.bind(this);
    this.endDrag = this.endDrag.bind(this);

    this.init();
  }

  /**
   * Initialize the slider
   */
  init() {
    console.log('🎠 [Slider] Initializing slider component...');
    
    this.cacheElements();
    this.calculateDimensions();
    this.bindEvents();
    this.updateSliderPosition();
    this.updateNavigation();
    
    if (this.options.enableDots) {
      this.createDots();
    }
    
    if (this.options.autoplay) {
      this.startAutoplay();
    }
    
    // Set initial adaptive height if enabled
    if (this.options.adaptiveHeight) {
      // Delay to ensure images and content are loaded
      setTimeout(() => {
        this.updateAdaptiveHeight();
      }, 300);
      
      // Also listen for image loads within slides
      if (this.slides) {
        this.slides.forEach((slide, index) => {
          const images = slide.querySelectorAll('img');
          images.forEach(img => {
            if (!img.complete) {
              img.addEventListener('load', () => {
                if (index === this.currentIndex) {
                  this.updateAdaptiveHeight();
                }
              });
            }
          });
        });
      }
    }
    
    this.dispatchEvent('slider:ready', {
      element: this.element,
      config: this.options,
      totalSlides: this.totalSlides
    });
    
    console.log('✅ [Slider] Slider initialized successfully with:', {
      totalSlides: this.totalSlides,
      slideWidth: this.slideWidth,
      slidesPerView: this.slidesPerView,
      currentIndex: this.currentIndex
    });
  }

  /**
   * Cache DOM elements
   */
  cacheElements() {
    // All queries are scoped to this.element to ensure we only get elements within this container
    this.track = this.element.querySelector(this.options.trackSelector);
    this.slides = Array.from(this.element.querySelectorAll(this.options.slideSelector)).filter(slide => 
      this.element.contains(slide)
    );
    this.prevButton = this.element.querySelector(this.options.prevButtonSelector);
    this.nextButton = this.element.querySelector(this.options.nextButtonSelector);
    this.dotsContainer = this.element.querySelector(this.options.dotsContainerSelector);
    
    this.totalSlides = this.slides.length;
    
    // Log for debugging if slides are found
    if (this.totalSlides > 0) {
      console.log('🎠 [Slider] Cached elements:', {
        containerId: this.element.id || this.element.getAttribute('data-testimonial-slider'),
        totalSlides: this.totalSlides,
        trackFound: !!this.track
      });
    }
  }

  /**
   * Calculate slider dimensions
   */
  calculateDimensions() {
    if (!this.track || this.totalSlides === 0) return;

    // Get slide width from CSS custom property or calculate from first slide
    const slideWidthPercent = this.getSlideWidthPercent();
    this.slideWidth = slideWidthPercent;
    this.slidesPerView = 100 / slideWidthPercent;

    console.log('🎠 [Slider] Calculated dimensions:', {
      slideWidthPercent,
      slidesPerView: this.slidesPerView,
      totalSlides: this.totalSlides,
      calculation: `100 / ${slideWidthPercent} = ${this.slidesPerView}`
    });
  }

  /**
   * Get slide width percentage from CSS custom property
   */
  getSlideWidthPercent() {
    const computedStyle = getComputedStyle(this.element);
    const isMobile = window.innerWidth < 768;
    
    const slideWidthVar = isMobile ? '--slide-width-mobile' : '--slide-width';
    const slideWidthValue = computedStyle.getPropertyValue(slideWidthVar).trim();
    
    if (slideWidthValue && slideWidthValue !== '') {
      return parseFloat(slideWidthValue);
    }
    
    // Fallback: calculate from first slide
    if (this.slides.length > 0) {
      const firstSlide = this.slides[0];
      const containerWidth = this.track.offsetWidth;
      const slideActualWidth = firstSlide.offsetWidth;
      return (slideActualWidth / containerWidth) * 100;
    }
    
    return 100; // Default to full width
  }

  /**
   * Bind all event listeners
   */
  bindEvents() {
    // Navigation buttons
    if (this.prevButton) {
      this.prevButton.addEventListener('click', () => this.prevSlide());
    }
    if (this.nextButton) {
      this.nextButton.addEventListener('click', () => this.nextSlide());
    }

    // Drag/swipe events
    if (this.options.enableDrag) {
      this.bindDragEvents();
    }

    // Keyboard navigation
    if (this.options.enableKeyboard) {
      this.element.addEventListener('keydown', this.handleKeydown);
    }

    // Window resize
    window.addEventListener('resize', this.handleResize);

    // Pause autoplay on hover
    if (this.options.autoplay) {
      this.element.addEventListener('mouseenter', () => this.pauseAutoplay());
      this.element.addEventListener('mouseleave', () => this.resumeAutoplay());
    }
  }

  /**
   * Bind drag/swipe events
   */
  bindDragEvents() {
    if (!this.track) return;

    // Mouse events
    this.track.addEventListener('mousedown', (event) => {
      if (this.totalSlides <= 1) return;
      event.preventDefault();
      this.startDrag(event.clientX, 'mouse');
    });

    document.addEventListener('mousemove', (event) => {
      if (!this.isDragging) return;
      this.handleDrag(event.clientX);
    });

    document.addEventListener('mouseup', (event) => {
      if (!this.isDragging) return;
      this.endDrag(event.clientX);
    });

    // Touch events
    this.track.addEventListener('touchstart', (event) => {
      if (this.totalSlides <= 1) return;
      const touch = event.touches[0];
      this.startDrag(touch.clientX, 'touch');
      this.dragStartY = touch.clientY; // Store Y position
    }, { passive: false });

    document.addEventListener('touchmove', (event) => {
      if (!this.isDragging) return;
      
      const touch = event.touches[0];
      const deltaX = Math.abs(touch.clientX - this.dragStartX);
      const deltaY = Math.abs(touch.clientY - this.dragStartY);
      
      // Determine if this is a horizontal drag
      if (!this.isHorizontalDrag && deltaX > 10) {
        this.isHorizontalDrag = deltaX > deltaY;
      }
      
      // Only prevent default if user is dragging horizontally
      if (this.isHorizontalDrag) {
        event.preventDefault();
        this.handleDrag(touch.clientX);
      }
    }, { passive: false });

    document.addEventListener('touchend', (event) => {
      if (!this.isDragging) return;
      
      const touch = event.changedTouches[0];
      
      // Only end drag if we were actually dragging horizontally
      if (this.isHorizontalDrag) {
        this.endDrag(touch.clientX);
      } else {
        // Reset drag state if it was vertical movement
        this.isDragging = false;
        this.isHorizontalDrag = false;
        this.track.style.transition = '';
        this.track.style.cursor = '';
        document.body.style.userSelect = '';
      }
    });
  }

  /**
   * Start dragging
   */
  startDrag(clientX, type) {
    this.isDragging = true;
    this.isHorizontalDrag = false; // Reset horizontal drag detection
    this.dragStartX = clientX;
    this.dragStartY = null; // Track Y position for direction detection
    this.dragStartIndex = this.currentIndex;
    
    // Compute starting translate position in % so we can drag freely (not locked per slide)
    const maxIndex = this.getMaxIndex();
    const hasScrollableOverflow = this.slidesPerView < this.totalSlides;
    const lastSlidePosition = hasScrollableOverflow
      ? (this.totalSlides - this.slidesPerView) * this.slideWidth
      : 0;
    const isAtEndPosition = this.currentIndex >= maxIndex && hasScrollableOverflow;
    this.dragStartTranslate = isAtEndPosition
      ? -lastSlidePosition
      : -this.currentIndex * this.slideWidth;
    
    this.track.style.transition = 'none';
    
    if (type === 'mouse') {
      this.track.style.cursor = 'grabbing';
      document.body.style.userSelect = 'none';
    }

    this.pauseAutoplay();
    
    this.dispatchEvent('slider:drag-start', {
      currentIndex: this.currentIndex,
      clientX
    });
  }

  /**
   * Handle dragging
   */
  handleDrag(clientX) {
    const deltaX = clientX - this.dragStartX;
    const containerWidth = this.track.offsetWidth;
    
    // Calculate drag progress as percentage
    const dragProgress = (deltaX / containerWidth) * 100;

    // Compute bounds in percent so we can't drag past the first/last slide
    const maxIndex = this.getMaxIndex();
    const hasScrollableOverflow = this.slidesPerView < this.totalSlides;
    const lastSlidePosition = hasScrollableOverflow
      ? (this.totalSlides - this.slidesPerView) * this.slideWidth
      : 0;

    const minTranslateX = hasScrollableOverflow ? -lastSlidePosition : 0; // furthest left
    const maxTranslateX = 0; // furthest right

    // Free-drag from the starting translate position, clamped to bounds
    const proposedTransform = this.dragStartTranslate + dragProgress;
    const dragTransform = Math.max(minTranslateX, Math.min(maxTranslateX, proposedTransform));

    this.track.style.transform = `translateX(${dragTransform}%)`;
  }

  /**
   * End dragging
   */
  endDrag(clientX) {
    this.isDragging = false;
    this.isHorizontalDrag = false; // Reset horizontal drag flag
    
    this.track.style.cursor = '';
    this.track.style.transition = '';
    document.body.style.userSelect = '';
    
    const deltaX = clientX - this.dragStartX;
    const containerWidth = this.track.offsetWidth;
    const dragPercent = (deltaX / containerWidth) * 100;
    const thresholdPx = containerWidth * 0.1; // 10% of container width
    
    const maxIndex = this.getMaxIndex();
    const hasScrollableOverflow = this.slidesPerView < this.totalSlides;
    const lastSlidePosition = hasScrollableOverflow
      ? (this.totalSlides - this.slidesPerView) * this.slideWidth
      : 0;
    const minTranslateX = hasScrollableOverflow ? -lastSlidePosition : 0;
    const maxTranslateX = 0;
    
    // Final translate after drag, clamped
    const finalTranslate = Math.max(minTranslateX, Math.min(maxTranslateX, this.dragStartTranslate + dragPercent));
    
    let newIndex = this.dragStartIndex;
    if (Math.abs(deltaX) > thresholdPx) {
      // Compute nearest slide from the final translate position
      const approxIndex = Math.round(Math.abs(finalTranslate) / this.slideWidth);
      const direction = deltaX < 0 ? 1 : -1; // left drag -> next, right drag -> prev
      // Ensure at least one step in the drag direction
      const proposedIndex = this.dragStartIndex + Math.max(1, Math.abs(approxIndex - this.dragStartIndex)) * direction;
      newIndex = Math.max(0, Math.min(maxIndex, proposedIndex));
    }
    
    this.goToSlide(newIndex);
    this.resumeAutoplay();
    
    this.dispatchEvent('slider:drag-end', {
      currentIndex: this.currentIndex,
      deltaX
    });
  }

  /**
   * Handle keyboard events
   */
  handleKeydown(event) {
    switch (event.key) {
      case 'ArrowLeft':
        event.preventDefault();
        this.prevSlide();
        break;
      case 'ArrowRight':
        event.preventDefault();
        this.nextSlide();
        break;
    }
  }

  /**
   * Handle window resize
   */
  handleResize() {
    this.calculateDimensions();
    this.updateSliderPosition();
    this.updateNavigation();
  }

  /**
   * Go to specific slide
   */
  goToSlide(index) {
    const maxIndex = this.getMaxIndex();
    
    console.log('🎠 [Slider] goToSlide called:', {
      requestedIndex: index,
      currentIndex: this.currentIndex,
      maxIndex,
      totalSlides: this.totalSlides,
      slidesPerView: this.slidesPerView,
      slideWidth: this.slideWidth
    });
    
    if (this.options.loop) {
      if (index < 0) {
        index = maxIndex;
      } else if (index > maxIndex) {
        index = 0;
      }
    } else {
      index = Math.max(0, Math.min(maxIndex, index));
    }

    if (index === this.currentIndex) return;

    this.currentIndex = index;
    this.updateSliderPosition();
    this.updateNavigation();
    this.updateDots();
    
    // Update adaptive height if enabled
    if (this.options.adaptiveHeight) {
      // Clear any pending height updates
      if (this._adaptiveHeightUpdateTimeout) {
        clearTimeout(this._adaptiveHeightUpdateTimeout);
      }
      
      // Listen for transition end to know when slide is fully visible
      let heightUpdateScheduled = false;
      const scheduleHeightUpdate = () => {
        if (heightUpdateScheduled) return;
        heightUpdateScheduled = true;
        
        // Clear fallback timeout if transitionend fired
        if (this._adaptiveHeightFallbackTimeout) {
          clearTimeout(this._adaptiveHeightFallbackTimeout);
          this._adaptiveHeightFallbackTimeout = null;
        }
        
        // Small delay after transition to ensure content is rendered
        const isMobile = window.innerWidth < 768;
        const delay = isMobile ? 150 : 100;
        
        this._adaptiveHeightUpdateTimeout = setTimeout(() => {
          // Double-check that currentIndex hasn't changed
          if (this.currentIndex === index) {
            this.updateAdaptiveHeight();
          }
        }, delay);
      };
      
      const handleTransitionEnd = (e) => {
        // Only handle transition end for transform (slide movement)
        if (e.target === this.track && (e.propertyName === 'transform' || !e.propertyName)) {
          scheduleHeightUpdate();
        }
      };
      
      // Add transition end listener
      if (this.track) {
        this.track.addEventListener('transitionend', handleTransitionEnd, { once: true });
        
        // Fallback timeout in case transitionend doesn't fire
        const isMobile = window.innerWidth < 768;
        const fallbackDelay = isMobile ? 500 : 400;
        this._adaptiveHeightFallbackTimeout = setTimeout(() => {
          scheduleHeightUpdate();
        }, fallbackDelay);
      }
    }
    
    this.dispatchEvent('slider:slide-changed', {
      currentIndex: this.currentIndex,
      totalSlides: this.totalSlides,
      slideWidth: this.slideWidth,
      element: this.element
    });
  }

  /**
   * Go to next slide - one by one with smart boundary handling
   */
  nextSlide() {
    const maxIndex = this.getMaxIndex();
    
    console.log('🎠 [Slider] Next slide:', {
      currentIndex: this.currentIndex,
      maxIndex,
      totalSlides: this.totalSlides,
      slidesPerView: this.slidesPerView
    });
    
    if (this.currentIndex >= maxIndex) {
      // Already at the end, handle based on loop setting
      if (this.options.loop) {
        this.goToSlide(0);
      }
      return;
    }
    
    // Normal one-by-one advancement
    this.goToSlide(this.currentIndex + 1);
  }

  /**
   * Go to previous slide - one by one
   */
  prevSlide() {
    console.log('🎠 [Slider] Previous slide:', {
      currentIndex: this.currentIndex,
      totalSlides: this.totalSlides,
      slidesPerView: this.slidesPerView
    });
    
    if (this.currentIndex <= 0) {
      // Already at the beginning, handle based on loop setting
      if (this.options.loop) {
        this.goToSlide(this.getMaxIndex());
      }
      return;
    }
    
    // Normal one-by-one going back
    this.goToSlide(this.currentIndex - 1);
  }

  /**
   * Get maximum slide index (position where last slide is fully visible)
   */
  getMaxIndex() {
    if (this.slidesPerView >= this.totalSlides) {
      console.log('🎠 [Slider] All slides visible, maxIndex = 0');
      return 0;
    }
    
    // Calculate position where last slide is fully visible (not cut off)
    // We want to show Math.floor(slidesPerView) full slides ending with the last slide
    const fullSlidesVisible = Math.floor(this.slidesPerView);
    const maxIndex = Math.max(0, this.totalSlides - fullSlidesVisible);
    
    console.log('🎠 [Slider] getMaxIndex calculation:', {
      totalSlides: this.totalSlides,
      slidesPerView: this.slidesPerView,
      fullSlidesVisible: fullSlidesVisible,
      calculation: `${this.totalSlides} - ${fullSlidesVisible}`,
      maxIndex: maxIndex
    });
    
    return maxIndex;
  }

  /**
   * Update slider position
   */
  updateSliderPosition() {
    if (!this.track) return;
    
    let translateX = -this.currentIndex * this.slideWidth;
    const maxIndex = this.getMaxIndex();
    
    // Special handling for the last position to ensure last slide is fully visible
    if (this.currentIndex >= maxIndex && this.slidesPerView < this.totalSlides) {
      // Calculate the exact position where the last slide is fully visible
      const lastSlidePosition = (this.totalSlides - this.slidesPerView) * this.slideWidth;
      translateX = -lastSlidePosition;
      
      console.log('🎠 [Slider] End position adjustment:', {
        currentIndex: this.currentIndex,
        maxIndex: maxIndex,
        totalSlides: this.totalSlides,
        slidesPerView: this.slidesPerView,
        lastSlidePosition: lastSlidePosition,
        adjustedTranslateX: translateX
      });
    }
    
    console.log('🎠 [Slider] Updating position:', {
      currentIndex: this.currentIndex,
      slideWidth: this.slideWidth,
      translateX: translateX,
      transform: `translateX(${translateX}%)`
    });
    
    this.track.style.transform = `translateX(${translateX}%)`;
    this.track.style.transition = 'transform 0.3s ease-out';
  }

  /**
   * Update navigation buttons
   */
  updateNavigation() {
    const maxIndex = this.getMaxIndex();
    
    if (this.prevButton) {
      this.prevButton.disabled = !this.options.loop && this.currentIndex === 0;
      this.prevButton.style.opacity = this.prevButton.disabled ? '0.5' : '1';
    }

    if (this.nextButton) {
      this.nextButton.disabled = !this.options.loop && this.currentIndex >= maxIndex;
      this.nextButton.style.opacity = this.nextButton.disabled ? '0.5' : '1';
    }
  }

  /**
   * Create dots navigation
   */
  createDots() {
    if (!this.dotsContainer) return;

    this.dotsContainer.innerHTML = '';
    this.dots = [];

    const maxIndex = Math.max(1, this.getMaxIndex() + 1);
    
    for (let i = 0; i < maxIndex; i++) {
      const dot = document.createElement('button');
      dot.className = 'slider-dot';
      dot.dataset.index = i;
      dot.addEventListener('click', () => this.goToSlide(i));
      
      this.dotsContainer.appendChild(dot);
      this.dots.push(dot);
    }

    this.updateDots();
  }

  /**
   * Update dots active state
   */
  updateDots() {
    this.dots.forEach((dot, index) => {
      if (index === this.currentIndex) {
        dot.classList.add('active');
      } else {
        dot.classList.remove('active');
      }
    });
  }

  /**
   * Update container height to match current slide (adaptive height)
   */
  updateAdaptiveHeight() {
    if (!this.slides || this.slides.length === 0) return;
    
    // Store the index we're measuring to ensure consistency
    const measuringIndex = this.currentIndex;
    
    // Get the current slide(s) that are visible - ensure it's within this container
    const currentSlide = this.slides[measuringIndex];
    if (!currentSlide) {
      console.warn('📏 [Slider] No slide found at index:', measuringIndex);
      return;
    }
    
    // Ensure the slide is actually within this container instance
    if (!this.element.contains(currentSlide)) {
      console.warn('📏 [Slider] Current slide is not within container:', {
        container: this.element,
        slide: currentSlide
      });
      return;
    }
    
    const container = this.element;
    
    // Use double requestAnimationFrame for better timing, especially on mobile
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        // Verify index hasn't changed
        if (this.currentIndex !== measuringIndex) {
          console.log('📏 [Slider] Index changed during measurement, skipping:', {
            measuringIndex,
            currentIndex: this.currentIndex
          });
          return;
        }
        
        // Find the actual content element to measure (shopify-block or testimonial-card)
        // Prefer measuring the inner content block rather than the wrapper
        let contentElement = currentSlide.querySelector('[id^="shopify-block-"]');
        if (!contentElement) {
          // Fallback to testimonial-card if no shopify-block found
          contentElement = currentSlide.querySelector('.testimonial-card');
        }
        if (!contentElement) {
          // Last resort: use the slide itself
          contentElement = currentSlide;
        }
        
        // Get the container width for accurate measurement
        const containerWidth = container.offsetWidth || container.clientWidth;
        
        if (containerWidth === 0) {
          console.warn('📏 [Slider] Container width is 0, cannot measure');
          return;
        }
        
        // Measure the actual visible element directly (more accurate than cloning)
        // First, ensure the slide is visible by checking its position
        const slideRect = currentSlide.getBoundingClientRect();
        const isSlideVisible = slideRect.width > 0 && Math.abs(slideRect.left) < containerWidth * 2;
        
        if (!isSlideVisible) {
          console.warn('📏 [Slider] Slide is not visible, waiting...', {
            slideRect,
            containerWidth
          });
          // Retry after a short delay
          setTimeout(() => {
            if (this.currentIndex === measuringIndex) {
              this.updateAdaptiveHeight();
            }
          }, 100);
          return;
        }
        
        // Get computed styles to preserve layout
        const computedStyle = window.getComputedStyle(contentElement);
        
        // Temporarily reset any height constraints to get natural height
        const originalHeight = contentElement.style.height;
        const originalMinHeight = contentElement.style.minHeight;
        const originalMaxHeight = contentElement.style.maxHeight;
        
        contentElement.style.height = 'auto';
        contentElement.style.minHeight = 'auto';
        contentElement.style.maxHeight = 'none';
        
        // Force reflow
        void contentElement.offsetHeight;
        
        // Measure the actual content height
        const contentHeight = Math.max(
          contentElement.offsetHeight || 0,
          contentElement.scrollHeight || 0,
          contentElement.getBoundingClientRect().height || 0
        );
        
        // Restore original styles
        contentElement.style.height = originalHeight;
        contentElement.style.minHeight = originalMinHeight;
        contentElement.style.maxHeight = originalMaxHeight;
        
        // The slide height should match the content height
        const slideHeight = contentHeight;
        
        // Verify index still matches
        if (this.currentIndex !== measuringIndex) {
          console.log('📏 [Slider] Index changed after measurement, skipping update');
          return;
        }
        
        // Only update if we got a valid height
        if (slideHeight > 0) {
          // Only update if height has changed to avoid unnecessary transitions
          const currentHeight = parseInt(container.style.height) || container.offsetHeight || 0;
          
          if (Math.abs(currentHeight - slideHeight) > 1) {
            // Set the container height with smooth transition - only this specific container
            container.style.transition = 'height 0.3s ease-out';
            container.style.height = slideHeight + 'px';
            
            // Also set height on track-wrapper if it exists - scoped to this container only
            const trackWrapper = container.querySelector('.slider-track-wrapper');
            if (trackWrapper && container.contains(trackWrapper)) {
              trackWrapper.style.transition = 'height 0.3s ease-out';
              trackWrapper.style.height = slideHeight + 'px';
            }
            
            // Set height on all slides to match the current slide's content height
            // This prevents layout shifts and ensures consistent height
            if (this.slides) {
              this.slides.forEach((slide) => {
                // Double-check that slide is within this container
                if (container.contains(slide)) {
                  slide.style.transition = 'height 0.3s ease-out';
                  slide.style.height = slideHeight + 'px';
                  slide.style.minHeight = slideHeight + 'px';
                }
              });
            }
            
            console.log('📏 [Slider] Adaptive height updated:', {
              containerId: container.id || container.getAttribute('data-testimonial-slider'),
              slideIndex: measuringIndex,
              slideHeight: slideHeight,
              previousHeight: currentHeight,
              containerHeight: container.style.height,
              trackWrapperHeight: trackWrapper ? trackWrapper.style.height : 'N/A',
              isMobile: window.innerWidth < 768,
              measuredElement: contentElement.id || contentElement.className,
              actualContentHeight: contentElement.offsetHeight
            });
          }
        } else {
          console.warn('📏 [Slider] Could not measure slide height:', {
            containerId: container.id || container.getAttribute('data-testimonial-slider'),
            slideIndex: measuringIndex,
            slideElement: currentSlide
          });
        }
      });
    });
    
    // Also update on resize to handle content changes
    if (!this._adaptiveHeightResizeHandler) {
      this._adaptiveHeightResizeHandler = () => {
        if (this.options.adaptiveHeight) {
          // Debounce resize events
          if (this._adaptiveHeightTimeout) {
            clearTimeout(this._adaptiveHeightTimeout);
          }
          this._adaptiveHeightTimeout = setTimeout(() => {
            this.updateAdaptiveHeight();
          }, 100);
        }
      };
      window.addEventListener('resize', this._adaptiveHeightResizeHandler);
    }
  }

  /**
   * Start autoplay
   */
  startAutoplay() {
    if (this.autoplayInterval) return;
    
    this.autoplayInterval = setInterval(() => {
      if (!this.isDragging) {
        this.nextSlide();
      }
    }, this.options.autoplayDelay);
  }

  /**
   * Pause autoplay
   */
  pauseAutoplay() {
    if (this.autoplayInterval) {
      clearInterval(this.autoplayInterval);
      this.autoplayInterval = null;
    }
  }

  /**
   * Resume autoplay
   */
  resumeAutoplay() {
    if (this.options.autoplay && !this.autoplayInterval) {
      this.startAutoplay();
    }
  }

  /**
   * Dispatch custom event
   */
  dispatchEvent(eventName, detail) {
    this.element.dispatchEvent(new CustomEvent(eventName, {
      detail,
      bubbles: true
    }));
  }

  /**
   * Destroy slider
   */
  destroy() {
    this.pauseAutoplay();
    
    // Clean up adaptive height resize handler
    if (this._adaptiveHeightResizeHandler) {
      window.removeEventListener('resize', this._adaptiveHeightResizeHandler);
      this._adaptiveHeightResizeHandler = null;
    }
    
    if (this._adaptiveHeightTimeout) {
      clearTimeout(this._adaptiveHeightTimeout);
      this._adaptiveHeightTimeout = null;
    }
    
    if (this._adaptiveHeightUpdateTimeout) {
      clearTimeout(this._adaptiveHeightUpdateTimeout);
      this._adaptiveHeightUpdateTimeout = null;
    }
    
    if (this._adaptiveHeightFallbackTimeout) {
      clearTimeout(this._adaptiveHeightFallbackTimeout);
      this._adaptiveHeightFallbackTimeout = null;
    }
    
    window.removeEventListener('resize', this.handleResize);
    
    if (this.track) {
      this.track.style.transform = '';
      this.track.style.transition = '';
      this.track.style.cursor = '';
    }
    
    console.log('🎠 [Slider] Slider destroyed');
  }
}

// Auto-initialize sliders with data-slider attribute
document.addEventListener('DOMContentLoaded', () => {
  const sliders = document.querySelectorAll('[data-slider]');
  sliders.forEach(slider => {
    // Parse configuration from data attribute
    let config = {};
    try {
      const configData = slider.dataset.sliderConfig;
      if (configData) {
        config = JSON.parse(configData);
      }
    } catch (e) {
      console.warn('Invalid slider config:', e);
    }
    
    new SliderComponent(slider, config);
  });
});

// Handle Shopify theme editor
document.addEventListener('shopify:section:load', (event) => {
  if (event.detail.sectionId) {
    const section = document.querySelector(`[data-section-id="${event.detail.sectionId}"]`);
    if (section && section.querySelector('[data-slider]')) {
      const slider = section.querySelector('[data-slider]');
      let config = {};
      try {
        const configData = slider.dataset.sliderConfig;
        if (configData) {
          config = JSON.parse(configData);
        }
      } catch (e) {
        console.warn('Invalid slider config:', e);
      }
      
      new SliderComponent(slider, config);
    }
  }
});

document.addEventListener('shopify:section:unload', (event) => {
  if (event.detail.sectionId) {
    const section = document.querySelector(`[data-section-id="${event.detail.sectionId}"]`);
    if (section && section.sliderInstance) {
      section.sliderInstance.destroy();
    }
  }
});

// Expose SliderComponent on window for dynamic initialization
if (typeof window !== 'undefined') {
  window.SliderComponent = SliderComponent;
}

export default SliderComponent; 